import { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faDownload } from "@fortawesome/free-solid-svg-icons";
import { formatLocalDate, formatDuration } from "./format";

// AI summary JSON keys we never want to render (already shown in the header).
const REMOVE_KEYS = ["ConversationDuration", "Conversation Duration"];

const SUB_TAB = { SUMMARY: "summary", TRANSCRIPT: "transcript" };

const isEmptyValue = (v) => {
  if (v === null || v === undefined) return true;
  if (typeof v === "string" && v.trim() === "") return true;
  if (Array.isArray(v) && v.length === 0) return true;
  if (typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0)
    return true;
  return false;
};

const formatTitle = (key) => {
  if (!key) return "";
  return String(key)
    .replace(/_/g, " ")
    .split(" ")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ""))
    .join(" ");
};

const formatObjectValue = (obj) => {
  if (!obj || typeof obj !== "object") return "";
  return Object.entries(obj)
    .map(([k, v]) => `${formatTitle(k)}: ${v}`)
    .join(", ");
};

// Summary JSON arrives wrapped — { choices: [{ message: { content: "JSON string" } }] }.
// content can already be parsed if the proxy did it for us.
const parseSummaryContent = (raw) => {
  if (!raw) return null;
  try {
    const messageContent =
      raw && raw.choices && raw.choices[0] && raw.choices[0].message && raw.choices[0].message.content;
    if (!messageContent) return null;
    if (typeof messageContent === "string") return JSON.parse(messageContent);
    return messageContent;
  } catch {
    return null;
  }
};

const formatSummaryAsText = (raw) => {
  const sections = parseSummaryContent(raw);
  if (!sections) return "";
  const lines = [];
  Object.entries(sections).forEach(([key, value]) => {
    if (REMOVE_KEYS.includes(key)) return;
    if (isEmptyValue(value)) return;
    lines.push(formatTitle(key));
    if (Array.isArray(value)) {
      value
        .filter((item) => (typeof item === "string" ? item.trim() !== "" : true))
        .forEach((item) => {
          lines.push(`  - ${typeof item === "object" ? formatObjectValue(item) : item}`);
        });
    } else if (typeof value === "object" && value !== null) {
      lines.push(formatObjectValue(value));
    } else {
      lines.push(String(value));
    }
    lines.push("");
  });
  return lines.join("\n").trim();
};

// Two-letter avatar initials from a participant name, e.g. "Binal Admin" -> "BA".
const initials = (name) => {
  const s = String(name || "").trim();
  if (!s) return "?";
  const parts = s.split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Elapsed seconds -> "MM:SS" or "H:MM:SS".
const secondsToTime = (sec) => {
  const n = Math.max(0, Math.floor(Number(sec) || 0));
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  const s = n % 60;
  const pad = (v) => String(v).padStart(2, "0");
  if (h > 0) return `${h}:${pad(m)}:${pad(s)}`;
  return `${pad(m)}:${pad(s)}`;
};

const formatTranscriptAsText = (lines) => {
  if (!Array.isArray(lines) || lines.length === 0) return "";
  const first = lines[0] && lines[0].SystemTimeStamp;
  return lines
    .map((row) => {
      const name = row?.Name || "Participant";
      const elapsed = first ? (row.SystemTimeStamp || 0) - first : 0;
      return `[${secondsToTime(elapsed)}] ${name}: ${row?.Text || ""}`;
    })
    .join("\n");
};

const triggerTextDownload = (filename, text) => {
  if (!text) return;
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "file.txt";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

const SummarySections = ({ raw }) => {
  const sections = useMemo(() => parseSummaryContent(raw), [raw]);
  if (!sections) {
    return <div className="ai-empty-inline">No summary content available.</div>;
  }
  const entries = Object.entries(sections).filter(
    ([k, v]) => !REMOVE_KEYS.includes(k) && !isEmptyValue(v)
  );
  if (entries.length === 0) {
    return <div className="ai-empty-inline">No summary content available.</div>;
  }
  return (
    <div className="ai-sections">
      {entries.map(([key, value]) => {
        if (Array.isArray(value)) {
          const items = value.filter((item) =>
            typeof item === "string" ? item.trim() !== "" : true
          );
          if (items.length === 0) return null;
          return (
            <div className="ai-section" key={key}>
              <div className="ai-section-title">{formatTitle(key)}</div>
              <ul className="ai-section-list">
                {items.map((item, i) => (
                  <li key={i}>
                    {typeof item === "object" ? formatObjectValue(item) : String(item)}
                  </li>
                ))}
              </ul>
            </div>
          );
        }
        if (typeof value === "object" && value !== null) {
          return (
            <div className="ai-section" key={key}>
              <div className="ai-section-title">{formatTitle(key)}</div>
              <div className="ai-section-text">{formatObjectValue(value)}</div>
            </div>
          );
        }
        return (
          <div className="ai-section" key={key}>
            <div className="ai-section-title">{formatTitle(key)}</div>
            <div className="ai-section-text">{String(value)}</div>
          </div>
        );
      })}
    </div>
  );
};

const TranscriptView = ({ raw }) => {
  const lines = Array.isArray(raw) ? raw : [];
  const first = lines[0] && lines[0].SystemTimeStamp;

  if (lines.length === 0) {
    return <div className="ai-empty-inline">No transcript available.</div>;
  }

  return (
    <div className="ai-transcript">
      {lines.map((row, i) => {
        const name = row?.Name || "Participant";
        const elapsed = first ? (row.SystemTimeStamp || 0) - first : 0;
        return (
          <div className="ai-tx-row" key={i}>
            <div className="ai-tx-avatar" aria-hidden="true">
              {initials(name)}
            </div>
            <div className="ai-tx-body">
              <div className="ai-tx-meta">
                <span className="ai-tx-name">{name}</span>
                <span className="ai-tx-time">{secondsToTime(elapsed)}</span>
              </div>
              <p className="ai-tx-text">{row?.Text || ""}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const SummaryItem = ({ item, defaultOpen }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  const [activeSub, setActiveSub] = useState(SUB_TAB.SUMMARY);

  const startDate = formatLocalDate(item.sessionStartTime);
  const duration = formatDuration(item.duration || 0);
  const headerText = startDate
    ? `${startDate} — Duration: ${duration}`
    : `Duration: ${duration}`;

  const summaryFileName =
    (item.aiSummary && item.aiSummary.fileName) || "summary.txt";
  const transcriptFileName =
    (item.transcription && item.transcription.fileName) || "transcript.txt";

  const hasSummary = !!item.aiSummaryContent;
  const hasTranscript =
    Array.isArray(item.transcriptionContent) && item.transcriptionContent.length > 0;

  const onDownload = (e) => {
    e.stopPropagation();
    if (activeSub === SUB_TAB.SUMMARY) {
      triggerTextDownload(summaryFileName, formatSummaryAsText(item.aiSummaryContent));
    } else {
      triggerTextDownload(
        transcriptFileName,
        formatTranscriptAsText(item.transcriptionContent)
      );
    }
  };

  const downloadDisabled =
    activeSub === SUB_TAB.SUMMARY ? !hasSummary : !hasTranscript;

  return (
    <div className={"ai-summary-item" + (open ? " open" : "")}>
      <button
        type="button"
        className="ai-summary-header"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <FontAwesomeIcon
          icon={faChevronDown}
          className={"ai-summary-chev" + (open ? " open" : "")}
        />
        <span className="ai-summary-header-text">{headerText}</span>
      </button>
      {open && (
        <div className="ai-summary-body">
          <div className="ai-subtabs-row">
            <div className="ai-subtabs" role="tablist">
              <button
                type="button"
                role="tab"
                className={"ai-subtab" + (activeSub === SUB_TAB.SUMMARY ? " active" : "")}
                onClick={() => setActiveSub(SUB_TAB.SUMMARY)}
              >
                Summary
              </button>
              <button
                type="button"
                role="tab"
                className={"ai-subtab" + (activeSub === SUB_TAB.TRANSCRIPT ? " active" : "")}
                onClick={() => setActiveSub(SUB_TAB.TRANSCRIPT)}
              >
                Transcript
              </button>
            </div>
            <div className="ai-subtabs-actions">
              <button
                type="button"
                className="ai-dl-btn"
                onClick={onDownload}
                disabled={downloadDisabled}
                title={activeSub === SUB_TAB.SUMMARY ? "Download Summary" : "Download Transcript"}
              >
                <FontAwesomeIcon icon={faDownload} className="ai-dl-icon" />
                <span className="ai-dl-label">Download</span>
              </button>
            </div>
          </div>
          <div className="ai-subtab-body">
            {activeSub === SUB_TAB.SUMMARY ? (
              hasSummary ? (
                <SummarySections raw={item.aiSummaryContent} />
              ) : (
                <div className="ai-empty-inline">No summary content available.</div>
              )
            ) : (
              <TranscriptView raw={item.transcriptionContent} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const AnalyzingState = () => (
  <div className="ai-analyzing">
    <div className="spinner" />
    <div className="ai-analyzing-title">Analyzing Your Call…</div>
    <div className="ai-analyzing-sub">
      Every great summary takes a moment. We&apos;re turning your call into something neat and readable.
    </div>
  </div>
);

const PendingBanner = ({ count }) => {
  const text = count === 1 ? "summary" : "summaries";
  return (
    <div className="ai-pending-banner">
      <div className="spinner spinner-sm" />
      <span>
        You have {count} AI {text} that need{count === 1 ? "s" : ""} to be processed before viewing.
      </span>
    </div>
  );
};

// Sort newest-first. sessionStartTime is the server-side UTC string used by
// the index — fall back to createdAt aliases if it's missing.
const sortByDateDesc = (files) => {
  const ts = (f) => {
    const v = f.sessionStartTime || f.createdAt || f.createDateTime || f.CreatedAt;
    if (!v) return 0;
    const t = Date.parse(String(v).replace(" ", "T"));
    return Number.isFinite(t) ? t : 0;
  };
  return [...files].sort((a, b) => ts(b) - ts(a));
};

const AISummaryPane = ({ summary, isLoading, error, onRetry }) => {
  if (isLoading) {
    return (
      <div className="loading-state">
        <div className="spinner" />
        <div>Loading…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ch-error">
        {error}
        {onRetry && (
          <div style={{ marginTop: 8 }}>
            <button type="button" className="ch-refresh-btn" onClick={onRetry}>
              Retry
            </button>
          </div>
        )}
      </div>
    );
  }

  const data = summary || {};
  const files = Array.isArray(data.sessionSummaryFiles) ? data.sessionSummaryFiles : [];
  const availableCount =
    Number.isFinite(Number(data.availableSummaryCount))
      ? Number(data.availableSummaryCount)
      : files.length;
  const pendingCount = Number.isFinite(Number(data.pendingSummaryCount))
    ? Number(data.pendingSummaryCount)
    : 0;

  if (files.length === 0 && pendingCount === 0) {
    return (
      <div className="empty-state ai-empty">
        <div className="es-msg">
          Turn on AI summaries to start capturing key moments from your calls.
        </div>
      </div>
    );
  }

  if (availableCount === 0 && pendingCount > 0) {
    return <AnalyzingState />;
  }

  const sorted = sortByDateDesc(files);
  return (
    <div className="ai-summary-list">
      {pendingCount > 0 && <PendingBanner count={pendingCount} />}
      {sorted.map((item, i) => (
        <SummaryItem
          key={(item.sessionStartTime || "") + "-" + i}
          item={item}
          defaultOpen={sorted.length === 1}
        />
      ))}
    </div>
  );
};

export default AISummaryPane;
